import { Analytics } from '@plaudit/analytics';

import {Slide} from './Slide';

export interface CarouselOptions {
	analyticsCategory?: string;
	carouselEl: Element;
	carouselClass: string;
	doIndicatorClick?: boolean;
	duration?: number;
	onChange?: Function;
	onNext?: Function;
	onPrev?: Function;
	onInit?: Function;
}

interface CarouselState {
	priorIndex?: number;
	currentIndex: number;
}

export abstract class CarouselAbstr {
	public readonly slides: Slide[] = [];

	private readonly _state: CarouselState[] = [{
		priorIndex: undefined,
		currentIndex: 0,
	}];

	private get previousState(): CarouselState {
		return this._state[this._state.length - 2];
	}
	private get state(): CarouselState {
		return this._state[this._state.length - 1];
	}

	private set state(newState: CarouselState) {
		this._state.push(newState);
	}

	protected readonly carouselClass: string;

	public readonly carouselEl: Element;
	protected readonly carouselInnerEl: Element;
	protected readonly carouselFrameEl: Element;

	protected readonly controls: Element|undefined;

	protected readonly duration: number|undefined;
	protected timeout: number|undefined;

	protected doIndicatorClick: boolean|undefined;

	protected analytics: Analytics|undefined;
	protected readonly analyticsCategory: string|undefined;

	protected eventLabels = {
		previous: 'Previous',
		next: 'Next',
		indicator: 'Indicator',
	};

	// NOTE: Empty function that options can override. An inexpensive way to avoid
	// having to test if function is defined from options
	protected onChange: Function = (fn: any) => fn;
	protected onNext: Function = (fn: any) => fn;
	protected onPrev: Function = (fn: any) => fn;
	protected onInit: Function = (fn: any) => fn;

	protected constructor(options: CarouselOptions) {
		this.carouselClass = options.carouselClass;
		this.carouselEl = options.carouselEl;

		this.carouselInnerEl = this.carouselEl.getElementsByClassName(this.carouselClass + '__inner')[0];
		this.carouselFrameEl = this.carouselEl.getElementsByClassName(this.carouselClass + '__frame')[0];

		this.onChange = options.onChange || this.onChange;
		this.onNext = options.onNext || this.onNext;
		this.onPrev = options.onPrev || this.onPrev;
		this.onInit = options.onInit || this.onInit;

		this.duration = options.duration;
		this.doIndicatorClick = options.doIndicatorClick;

		this.analyticsCategory = options.analyticsCategory;
		// this.analytics = this.analyticsCategory ? Analytics.getInstance() : undefined;

		/* If input has focus, pause carousel */
		this.carouselEl.addEventListener('focusin', () => {
			this.stopAutoAdvance();
		});
		this.carouselEl.addEventListener('focusout', () => {
			this.initAutoAdvance();
		});
	}

	protected abstract init(): void;

	public activatePrevIndex(): void {
		const newIndex: number = this.prevIndex();
		this.activateByIndex(newIndex);

		this.onPrev();
	}
	public activateNextIndex(): void {
		const newIndex: number = this.nextIndex();
		this.activateByIndex(newIndex);

		this.onNext(newIndex);
	}

	/** Activates slide, indicator, and caption by selected index */
	// NOTE: Number of slides should always equal number of indicators.
	// Maybe should refactor to avoid this?
	public activateByIndex(newIndex: number): void {
		if (this.state.currentIndex === newIndex) return;

		// Remove existing outgoing classes. Pull into own method?
		this.slides.forEach((slide: Slide, index: number) => {
			if (index === newIndex) {
				slide.activate();
			} else if (slide.isActive) {
				slide.deactivate();
			} else if (slide.isOutgoing) {
				slide.clear();
			}
		});

		// Set new current index
		this.state = {
			priorIndex: this.state.currentIndex,
			currentIndex: newIndex,
		};

		this.onChange(newIndex);

		this.initAutoAdvance();
	}

	/** Remove all active/outgoing classes from slides. */
	public deactiveAll(): void {
		for (const el of this.carouselEl.getElementsByClassName('outgoing')) {
			el.classList.remove('outgoing');
		}
		for (const el of this.carouselEl.getElementsByClassName('active')) {
			el.classList.remove('active');
		}
		this.state.currentIndex = 0;
	}

	/** Get prev index. Loops around if at start */
	public prevIndex(): number {
		return this.state.currentIndex === 0 ? this.slides.length - 1 : this.state.currentIndex - 1;
	}

	/** Get next index. Loops around if at end */
	public nextIndex(): number {
		return this.state.currentIndex === this.slides.length - 1 ? 0 : this.state.currentIndex + 1;
	}

	/** Starts automatic progression if duration specified. */
	protected initAutoAdvance(): void {
		if (this.duration) {
			this.stopAutoAdvance();
			this.carouselFrameEl.classList.toggle('featureCarousel–autoAdvanceActive', true);

			this.timeout = window.setTimeout(() => this.activateNextIndex(), this.duration);
		}
	}

	protected stopAutoAdvance(): void {
		if (this.timeout) {
			clearTimeout(this.timeout);
			this.carouselFrameEl.classList.remove('featureCarousel–autoAdvanceActive');
		}
	}

	protected buildControls(): HTMLElement {
		const controlsAbstr = document.createElement('div');
		const controlsNextAbstr = document.createElement('button');
		const controlsPrevAbstr = document.createElement('button');

		controlsAbstr.className = `${this.carouselClass}__controls`;
		controlsNextAbstr.className = `${this.carouselClass}__control control--next`;
		controlsPrevAbstr.className = `${this.carouselClass}__control control--prev`;
		controlsPrevAbstr.addEventListener('click', () => {
			this.activatePrevIndex();
			this.trackControlEvent(this.eventLabels.previous);
		});
		controlsNextAbstr.addEventListener('click', () => {
			this.activateNextIndex();
			this.trackControlEvent(this.eventLabels.next);
		});

		controlsAbstr.append(controlsPrevAbstr, controlsNextAbstr);

		return controlsAbstr;
	}

	protected buildIndicators(activeIndex: number) {
		if (this.slides.length > 1) {
			const indicatorTrayAbstr = document.createElement('div');
			indicatorTrayAbstr.classList.toggle(`${this.carouselClass}__indicators`, true);

			for (let i: number = 0; i < this.slides.length; i++) {
				const indicatorAbstr = document.createElement(this.doIndicatorClick ? 'button': 'div');

				const indicatorLabel = this.slides[i].element.dataset.indicatorLabel;

				indicatorAbstr.classList.toggle(`${this.carouselClass}__indicator`, true);
				indicatorAbstr.classList.toggle(`${this.carouselClass}__indicator--${i + 1}`, true);
				if (i === activeIndex) {
					indicatorAbstr.classList.toggle('active', true);
				}

				// const indicatorTextEl = document.createElement('span');
				// indicatorTextEl.innerHTML = `<span>${i + 1}</span> ${indicatorLabel}`;
				// indicatorAbstr.appendChild(indicatorTextEl);
				indicatorAbstr.innerHTML = `<span>0${i + 1 + '.'}</span>${indicatorLabel}`;

				const indicator = indicatorTrayAbstr.appendChild(indicatorAbstr);

				if (this.doIndicatorClick) {
					indicator.addEventListener('click', () => {
						this.trackControlEvent(this.eventLabels.indicator);
						this.activateByIndex(i);
					});
				}
			}

			// NOTE: Needed for transition
			window.setTimeout(() => {
				this.carouselEl.classList.toggle(this.carouselClass + '--hasIndicators', true);
			}, 50);

			return indicatorTrayAbstr;
		}
	}

	protected trackControlEvent = (label: string) => {
		if (this.analytics && this.analyticsCategory) {
			this.analytics.trackEvent(this.analyticsCategory, 'Control Click', label);
		}
	}
}
